import React, { useState, useEffect } from 'react';
import { userService } from '../services/api';
import './UserManagement.css';

function UserManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'procesador',
    companyCuit: '',
    canCreateInvoices: true,
    canEditInvoices: true,
    canManageMasterData: false,
    approvalLimit: 1000000
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.listUsers();
      setUsers(response.users);
      setError('');
    } catch (err) {
      setError('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          role: formData.role,
          companyCuit: formData.companyCuit,
          canCreateInvoices: formData.canCreateInvoices,
          canEditInvoices: formData.canEditInvoices,
          canManageMasterData: formData.canManageMasterData
        };
        if (formData.role === 'supervisor') {
          updateData.approvalLimit = parseFloat(formData.approvalLimit);
        }
        await userService.updateUser(editingUser.userId, updateData);
      } else {
        // Create new user
        if (!formData.password) {
          setError('Contraseña requerida');
          return;
        }
        await userService.createUser({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          companyCuit: formData.companyCuit,
          canCreateInvoices: formData.canCreateInvoices,
          canEditInvoices: formData.canEditInvoices,
          canManageMasterData: formData.canManageMasterData,
          approvalLimit: formData.role === 'supervisor' ? parseFloat(formData.approvalLimit) : undefined
        });
      }

      await loadUsers();
      handleCancel();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar usuario');
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      role: u.role,
      companyCuit: u.companyCuit,
      canCreateInvoices: u.canCreateInvoices ?? (u.role !== 'administrador'),
      canEditInvoices: u.canEditInvoices ?? (u.role !== 'administrador'),
      canManageMasterData: u.canManageMasterData ?? (u.role === 'administrador'),
      approvalLimit: u.approvalLimit || 1000000,
      password: ''
    });
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      return;
    }

    try {
      await userService.deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'procesador',
      companyCuit: '',
      canCreateInvoices: true,
      canEditInvoices: true,
      canManageMasterData: false,
      approvalLimit: 1000000
    });
    setError('');
  };

  const getRoleLabel = (role) => {
    const labels = {
      procesador: 'Procesador',
      supervisor: 'Supervisor',
      administrador: 'Administrador'
    };
    return labels[role] || role;
  };

  const getStatusBadge = (active) => {
    return (
      <span className={`badge ${active ? 'badge-approved' : 'badge-rejected'}`}>
        {active ? 'Activo' : 'Inactivo'}
      </span>
    );
  };

  if (loading) {
    return <div className="container loading">Cargando usuarios...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Gestión de Usuarios</h1>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            + Nuevo Usuario
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {showForm ? (
        <div className="card">
          <h2>{editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nombre Completo *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email {editingUser && '(no editable)'}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  required={!editingUser}
                  disabled={!!editingUser}
                />
              </div>
            </div>

            {!editingUser && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Contraseña *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="form-control"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                    minLength="8"
                  />
                  <small>Mínimo 8 caracteres, mayúsculas, minúsculas, números y símbolos</small>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="role">Rol *</label>
                <select
                  id="role"
                  name="role"
                  className="form-control"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="procesador">Procesador</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="administrador">Administrador</option>
                </select>
              </div>

              {formData.role === 'supervisor' && (
                <div className="form-group">
                  <label htmlFor="approvalLimit">Límite de Aprobación (ARS)</label>
                  <input
                    type="number"
                    id="approvalLimit"
                    name="approvalLimit"
                    className="form-control"
                    value={formData.approvalLimit}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="canCreateInvoices"
                  name="canCreateInvoices"
                  checked={!!formData.canCreateInvoices}
                  onChange={(e) => setFormData({ ...formData, canCreateInvoices: e.target.checked })}
                />
                <label htmlFor="canCreateInvoices" style={{ marginBottom: 0 }}>
                  Puede crear facturas
                </label>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="canEditInvoices"
                  name="canEditInvoices"
                  checked={!!formData.canEditInvoices}
                  onChange={(e) => setFormData({ ...formData, canEditInvoices: e.target.checked })}
                />
                <label htmlFor="canEditInvoices" style={{ marginBottom: 0 }}>
                  Puede editar facturas
                </label>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="canManageMasterData"
                  name="canManageMasterData"
                  checked={!!formData.canManageMasterData}
                  onChange={(e) => setFormData({ ...formData, canManageMasterData: e.target.checked })}
                />
                <label htmlFor="canManageMasterData" style={{ marginBottom: 0 }}>
                  Puede gestionar Master Data
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="companyCuit">CUIT Empresa</label>
                <input
                  type="text"
                  id="companyCuit"
                  name="companyCuit"
                  className="form-control"
                  value={formData.companyCuit}
                  onChange={handleChange}
                  placeholder="20-12345678-9"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="card">
          {users.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666' }}>
              No hay usuarios registrados. Crea el primero.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Crear</th>
                  <th>Editar</th>
                  <th>Master Data</th>
                  <th>Límite</th>
                  <th>Empresa</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{getRoleLabel(u.role)}</td>
                    <td>{(u.canCreateInvoices ?? (u.role !== 'administrador')) ? 'Sí' : 'No'}</td>
                    <td>{(u.canEditInvoices ?? (u.role !== 'administrador')) ? 'Sí' : 'No'}</td>
                    <td>{(u.canManageMasterData ?? (u.role === 'administrador')) ? 'Sí' : 'No'}</td>
                    <td>
                      {u.role === 'supervisor'
                        ? `$${u.approvalLimit?.toLocaleString('es-AR') || 'N/A'}`
                        : '-'}
                    </td>
                    <td>{u.companyCuit || '-'}</td>
                    <td>{getStatusBadge(u.active)}</td>
                    <td>
                      <button
                        onClick={() => handleEdit(u)}
                        className="btn btn-secondary btn-sm"
                        style={{ marginRight: '5px' }}
                      >
                        Editar
                      </button>
                      {u.email !== user.email && (
                        <button
                          onClick={() => handleDelete(u.userId)}
                          className="btn btn-danger btn-sm"
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default UserManagement;
